import { useEffect, useRef, useState } from 'react';
import { rnnoiseProcessor } from '../features/AudioProcessing/processors/RnnoiseProcessor';

export const useMicrophoneTrack = (device, isEnabled = true) => {
  const audioTrackRef = useRef(null);
  const audioContextRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deviceId, setDeviceId] = useState(null);


  useEffect(() => {
    if (device?.deviceId)
      setDeviceId(device.deviceId)
  }, [device?.deviceId]);

  useEffect(() => {
    setError(null);
    let isMounted = true;

    const createAudioTrack = async () => {
      try {
        setLoading(true);
        setError(null);

        // Cleanup previous track and context if they exist
        if (audioTrackRef.current) {
          audioTrackRef.current.stop();
          audioTrackRef.current = null;
        }
        if (audioContextRef.current) {
          if (audioContextRef.current.state !== 'closed') {
            await audioContextRef.current.close().catch(err =>
              console.warn('[RNNoise] Error closing previous AudioContext:', err)
            );
          }
          audioContextRef.current = null;
        }

        // Check if microphone is disabled or no device selected
        if (!isEnabled || !deviceId) {
          setLoading(false);
          return;
        }

        // 1. Get raw microphone stream
        // Disable browser noise suppression - we'll use RNNoise
        // Keep echo cancellation enabled (RNNoise is NS only)
        const rawStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: false,
            autoGainControl: true,
            sampleRate: 48000, // RNNoise works at 48kHz
            ...(deviceId && deviceId !== 'default' && { deviceId: { exact: deviceId } })
          }
        });

        if (!isMounted) {
          rawStream.getTracks().forEach(track => track.stop());
          return;
        }

        // 2. Try to initialize RNNoise
        try {
          await rnnoiseProcessor.init();
        } catch (rnnoiseError) {
          console.warn('[RNNoise] Init failed, falling back to raw stream:', rnnoiseError);
          // Fallback to raw stream if RNNoise fails
          const audioTrack = rawStream.getAudioTracks()[0];
          audioTrackRef.current = audioTrack;
          setLoading(false);
          return;
        }

        // 3. Create AudioContext for Web Audio API graph
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioContext({ sampleRate: 48000 }); // RNNoise needs 48kHz
        audioContextRef.current = ctx;

        const source = ctx.createMediaStreamSource(rawStream);
        const destination = ctx.createMediaStreamDestination();

        // 4. Create and connect RNNoise worklet node
        const rnnoiseNode = await rnnoiseProcessor.createNode(ctx);

        source.connect(rnnoiseNode);
        rnnoiseNode.connect(destination);

        // 5. Get the processed track
        const processedTrack = destination.stream.getAudioTracks()[0];

        // Keep reference to original track to stop it later
        const originalTrack = rawStream.getAudioTracks()[0];

        // Monkey-patch stop to ensure everything is cleaned up
        const originalStop = processedTrack.stop.bind(processedTrack);
        processedTrack.stop = () => {
          originalStop();
          originalTrack.stop();
          if (ctx.state !== 'closed') {
            ctx.close().catch(err => console.warn('[RNNoise] Error closing AudioContext:', err));
          }
        };

        audioTrackRef.current = processedTrack;
        setLoading(false);

      } catch (err) {
        if (!isMounted) return;

        setLoading(false);

        // Categorize errors for better handling
        if (err.name === 'NotAllowedError') {
          setError({ type: 'permission', message: 'Microphone permission denied' });
        } else if (err.name === 'NotFoundError') {
          setError({ type: 'not-found', message: 'Microphone device not found' });
        } else if (err.name === 'NotReadableError') {
          setError({ type: 'in-use', message: 'Microphone is already in use' });
        } else {
          setError({ type: 'generic', message: err.message || 'Failed to access microphone' });
        }

        console.error('Microphone track error:', err);
      }
    };

    createAudioTrack();

    // Cleanup function
    return () => {
      isMounted = false;
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        audioTrackRef.current = null;
      }
      if (audioContextRef.current) {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close().catch(err =>
            console.warn('[RNNoise] Error closing AudioContext in cleanup:', err)
          );
        }
        audioContextRef.current = null;
      }
      setLoading(false);
    };
  }, [deviceId, isEnabled]);

  return {
    track: audioTrackRef.current,
    loading,
    error
  };
};